import React from "react"
import { Controller, useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"

import { FileUpload } from "shared/ui/file-upload/FileUpload"

import { FeedbackOverlayFormData } from "./FeedbackOverlay"
import { useFeedbackImageUpload } from "./hooks/useFeedbackImageUpload"

interface FeedbackImageFieldProps {
  onChange: (value: FeedbackOverlayFormData["attachments"]) => void
  value: FeedbackOverlayFormData["attachments"]
}

const FeedbackImageField = React.memo<FeedbackImageFieldProps>(
  ({ onChange, value }) => {
    const { t } = useTranslation()
    const {
      displayFiles,
      accept,
      dataFormats,
      isLoading,
      maxFilesCount,
      handleUpload,
      handleDelete
    } = useFeedbackImageUpload({
      onAttachmentsChange: onChange,
      currentAttachments: value || []
    })

    return (
      <FileUpload.Root
        isLoading={isLoading}
        multiple={false}
        label={
          maxFilesCount > 1
            ? t("Feedback.AttachImages")
            : t("Feedback.AttachImage")
        }
        loadingFileCount={0}
        files={displayFiles}
        accept={accept}
        dataFormats={dataFormats}
        onUpload={handleUpload}
        onDelete={handleDelete}
      >
        <FileUpload.Label />
        <FileUpload.List />
        {displayFiles.length < maxFilesCount && (
          <FileUpload.Input variant="minimal" />
        )}
      </FileUpload.Root>
    )
  }
)

export const FeedbackImageInput = React.memo(() => {
  const { control } = useFormContext<FeedbackOverlayFormData>()

  return (
    <Controller
      name="attachments"
      control={control}
      render={({ field: { onChange, value } }) => (
        <FeedbackImageField onChange={onChange} value={value} />
      )}
    />
  )
})
